import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { Button } from "../../../components";
import AiInterviewerSectionTitle from "./AiInterviewerSectionTitle";
import { AiInterviewerFormValues } from "./types";

type AiInterviewerCompletionNotificationsProps = {
  readOnly: boolean;
};

/**
 * Completion notifications form section.
 * Uses the parent form context (AiInterviewerFormValues) to manage
 * completionNotificationEmails as part of the unified form state.
 * Changes are saved when the user clicks "Save changes" in the header.
 */
const AiInterviewerCompletionNotifications: React.FC<
  AiInterviewerCompletionNotificationsProps
> = ({ readOnly }) => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<AiInterviewerFormValues>();

  const { fields, append, remove } = useFieldArray({
    control,
    // Use completionNotificationEmails directly - we need to cast since it's string[]
    // but useFieldArray expects objects with id
    name: "completionNotificationEmails" as any,
  });

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return (
    <Box>
      <AiInterviewerSectionTitle title="Completion notifications" />
      <Text fontSize="sm" color="gray.600" mt={2} mb={4}>
        Receive email notifications when candidates complete their interviews.
        These notifications are only sent for ATS-less interviews (static link
        mode).
      </Text>

      <VStack spacing={4} align="stretch">
        {fields.length === 0 && (
          <Text fontSize="sm" color="gray.500">
            No notification emails configured.
          </Text>
        )}
        {fields.map((field, index) => {
          const fieldErrors = (errors.completionNotificationEmails as any)?.[
            index
          ];
          return (
            <FormControl key={field.id} isInvalid={!!fieldErrors}>
              <FormLabel fontSize="sm">Email {index + 1}</FormLabel>
              <Flex gap={2}>
                <Input
                  {...register(`completionNotificationEmails.${index}`, {
                    validate: (value: string) => {
                      const trimmed = value?.trim() ?? "";
                      if (trimmed.length === 0) return true; // Allow empty
                      return (
                        emailRegex.test(trimmed) ||
                        "Please enter a valid email address"
                      );
                    },
                  })}
                  placeholder="recruiter@example.com"
                  fontSize="sm"
                  isDisabled={readOnly}
                />
                <Button
                  type="button"
                  onClick={() => remove(index)}
                  size="sm"
                  variant="ghost"
                  isDisabled={readOnly}
                >
                  Remove
                </Button>
              </Flex>
              <FormErrorMessage>
                {fieldErrors?.message || fieldErrors?.root?.message}
              </FormErrorMessage>
            </FormControl>
          );
        })}

        <Box>
          <Button
            type="button"
            onClick={() => append("" as any)}
            size="sm"
            variant="outline"
            isDisabled={readOnly}
          >
            Add email
          </Button>
        </Box>
      </VStack>
    </Box>
  );
};

export default AiInterviewerCompletionNotifications;
